import { Button, CIActionGuard, SearchInput, useUserPermission } from 'ccs-ui-components';
import { ChangeEvent } from 'react';
import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { requiredAuthPermission } from '../../../../common/constants/UserPermissionConstants';
import { type JourneyDesignerServiceReturnResume, saveJourneyDesignerReturnContext } from '../../../../common/utilities/JourneyDesignerReturnContext';
import { DraggingItemType, SidebarDataLoadingState } from '../../../modules/journey-canvas/utility/TypeDefs';
import { AssociatedService, Service } from '../../../store/_api/TypeDefs';
import { getServiceTypeIcon } from '../../journey-builder-footer/utility/Helpers';
import JourneyCanvasListItem from '../../journey-canvas-event-item/JourneyCanvasListItem';

import dragIcon from '../../../assets/images/DragIcon.png';
import classes from './AllServices.module.scss';

interface AllServicesProps {
  hideSidebar: () => void;
  serviceFilterHandler: (e: ChangeEvent<HTMLInputElement>) => void;
  filteredServices: Service[] | undefined;
  allServices: Service[];
  serviceClickHandler: (service: Service | AssociatedService, type: DraggingItemType) => void;
  dragStartHandler: (item: Service | AssociatedService, itemType: DraggingItemType, ev: React.DragEvent) => void;
  allServicesDataLoad: {
    loadingMore: boolean;
    page: number;
    noLoadMore: boolean;
    failed: boolean;
  };
  loadMoreServices: () => void;
  searchTerm: string;
  sidebarDataLoadingState: SidebarDataLoadingState;
  invalidSearch: boolean;
  journeyName?: string;
  journeyVersion?: number;
  journeyMode?: string;
  /** When set, returning from Control Plane re-opens this step and the service sidebar. */
  stepCardUUID?: string;
  /** Human-readable step name used to round-trip the step in the URL on return. */
  stepName?: string;
  /** Flow item that was highlighted when the user opened this sidebar; restored on return. */
  selectedFlowItemNodeId?: string;
  /** Set when the sidebar is opened from within CEM design; restored on return. */
  cemRootNodeId?: string;
  /** True when the sidebar is rendered inside the CEM editor. */
  designingCEM?: boolean;
}

const AllServices = ({ hideSidebar, serviceFilterHandler, filteredServices, allServices, serviceClickHandler, dragStartHandler, allServicesDataLoad, loadMoreServices, searchTerm, sidebarDataLoadingState, invalidSearch, journeyName = '', journeyVersion = 0, journeyMode = '', stepCardUUID, stepName, selectedFlowItemNodeId, cemRootNodeId, designingCEM = false }: AllServicesProps) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const { permissionObject } = useUserPermission();

  const currentReturnPath = window.location.pathname + window.location.search;

  const jdReturnContextBase = () => {
    let serviceReturnResume: JourneyDesignerServiceReturnResume;
    if (designingCEM && stepCardUUID && cemRootNodeId) {
      serviceReturnResume = {
        kind: 'cem',
        stepNodeId: stepCardUUID,
        stepName,
        cemRootNodeId,
        selectedFlowItemNodeId,
      };
    } else if (stepCardUUID) {
      serviceReturnResume = { kind: 'step', stepNodeId: stepCardUUID, stepName, selectedFlowItemNodeId };
    } else {
      serviceReturnResume = { kind: 'none' };
    }
    return {
      returnPath: currentReturnPath,
      journeyName,
      journeyVersion,
      mode: journeyMode,
      serviceReturnResume,
      resumeSidebar: 'ALL_SERVICES' as const,
    };
  };

  const navigateToCreateService = () => {
    saveJourneyDesignerReturnContext(jdReturnContextBase());
    navigate('/control-plane/services/createService', { state: { from: currentReturnPath } });
  };

  const navigateToEditService = (service: Service, e: React.MouseEvent) => {
    e.stopPropagation();
    saveJourneyDesignerReturnContext(jdReturnContextBase());
    navigate(`/control-plane/services/viewServiceDetails/${service.serviceName}/${service.version}/view`, { state: { from: currentReturnPath } });
  };

  const canEditServices = CIActionGuard(
    [requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve],
    permissionObject.permission
  );

  return (
    <>
      <div className={classes['sidebar-heading']} data-testid="allServicesSideBarHeader">
        <div className={classes['close-sidebar']}>
          <p className={classes['sidebar-title']} data-testid="sidebarTitle">{translate('journey_builder_component.title_service')}</p>
          <Button variant="tertiary" data-testid="sidebarCloseBtn" onClick={hideSidebar}>
            <i className="fa-solid fa-xmark" />
          </Button>
        </div>
        <div className={classes['service-action']}>
          <p>{translate('journey_builder_component.all_services')}</p>
          {canEditServices && (
            <Button
              variant="primary"
              className={classes['create-service-btn']}
              onClick={navigateToCreateService}
              data-testid="createServiceFromJD"
            >
              <i className="fa-solid fa-plus" /> {translate('journey_builder_component.btn_create_service')}
            </Button>
          )}
        </div>
        <div className={`mb-2 ${classes.search}`}>
          <SearchInput
            data-testid="eventListFilterSearchBox"
            placeholder={translate('journey_builder_component.lbl_search') ?? ''}
            type="search"
            onChange={serviceFilterHandler}
            value={searchTerm}
            label="search"
          />
          {
            invalidSearch && (
              <p className={classes['invalid-search']}>{translate('journey_builder_component.msg_invalid_search')}</p>
            )
          }
        </div>
      </div>
      {
        sidebarDataLoadingState === SidebarDataLoadingState.NONE && (
          <Col data-testid="allServicesSideBar">
            {(filteredServices?.length !== 0 || allServices.length !== 0) && (
              <>
                {(filteredServices ?? allServices).map((service: Service) => (
                  <div className={classes['service-item-wrapper']} key={`${service.serviceName}_${service.version}`}>
                    <JourneyCanvasListItem
                      name={service.serviceName}
                      version={service.version}
                      description={service.serviceDescription}
                      icon={getServiceTypeIcon(service.serviceType)}
                      onClick={() => serviceClickHandler(service, DraggingItemType.SERVICE)}
                      onDragStart={(ev) => dragStartHandler(service, DraggingItemType.SERVICE, ev)}
                      dragIcon={dragIcon}
                    />
                    {canEditServices && (
                      <Button
                        variant="tertiary"
                        className={classes['edit-service-btn']}
                        onClick={(e) => navigateToEditService(service, e)}
                        data-testid={`editService_${service.serviceName}`}
                        title={translate('journey_builder_component.btn_edit_service') ?? ''}
                      >
                        <i className="fa-solid fa-pen-to-square" />
                      </Button>
                    )}
                  </div>
                ))}
                {allServicesDataLoad.loadingMore && <div className="text-center mt-4 mb-2"><span className="loader" /></div>}
                {allServicesDataLoad.failed && (
                  <Button variant="secondary" className={classes['load-more-btn']} onClick={loadMoreServices} data-testid="loadServices">
                    {translate('journey_builder_component.btn_load_more')}
                  </Button>
                )}
              </>
            )}
            {(filteredServices && filteredServices.length === 0 && searchTerm.length > 0) && (
              <p className={classes['no-data']}>
                {translate('journey_builder_component.txt_no_services_in_search', {
                  replace: { term: searchTerm }
                })}
              </p>
            )}
            {(!filteredServices && allServices.length === 0) && (
              <p className={classes['no-data']}>
                {translate('journey_builder_component.txt_no_services')}
              </p>
            )}
          </Col>
        )
      }
    </>
  );
};

export default AllServices;
